import { __rest } from "tslib";
import * as React from 'react';
import KeyCode from 'rc-util/lib/KeyCode';
import pickAttrs from 'rc-util/lib/pickAttrs';
import useMemo from 'rc-util/lib/hooks/useMemo';
import classNames from 'classnames';
import List from 'rc-virtual-list';
const OptionList = ({ prefixCls, id, flattenOptions, childrenAsData, values, searchValue, multiple, defaultActiveFirstOption, height, itemHeight, notFoundContent, open, virtual, onSelect, onToggleOpen, onActiveValue, onScroll, onMouseEnter, }, ref) => {
    const itemPrefixCls = `${prefixCls}-item`;
    const memoFlattenOptions = useMemo(() => flattenOptions, [open, flattenOptions], (prev, next) => next[0] && prev[1] !== next[1]);
    const listRef = React.useRef(null);
    const onListMouseDown = event => {
        event.preventDefault();
    };
    const scrollIntoView = (index) => {
        if (listRef.current) {
            listRef.current.scrollTo({ index });
        }
    };
    const getEnabledActiveIndex = (index, offset = 1) => {
        const len = memoFlattenOptions.length;
        for (let i = 0; i < len; i += 1) {
            const current = (index + i * offset + len) % len;
            const { group, data } = memoFlattenOptions[current];
            if (!group && !data.disabled) {
                return current;
            }
        }
        return -1;
    };
    const [activeIndex, setActiveIndex] = React.useState(() => getEnabledActiveIndex(0));
    const setActive = (index, fromKeyboard = false) => {
        setActiveIndex(index);
        const info = { source: fromKeyboard ? 'keyboard' : 'mouse' };
        const flattenItem = memoFlattenOptions[index];
        if (!flattenItem) {
            onActiveValue(null, -1, info);
            return;
        }
        onActiveValue(flattenItem.data.value, index, info);
    };
    React.useEffect(() => {
        setActive(defaultActiveFirstOption !== false ? getEnabledActiveIndex(0) : -1);
    }, [memoFlattenOptions.length, searchValue]);
    React.useEffect(() => {
        var _a;
        const timeoutId = setTimeout(() => {
            if (!multiple && open && values.size === 1) {
                const value = Array.from(values)[0];
                const index = memoFlattenOptions.findIndex(({ data }) => data.value === value);
                setActive(index);
                scrollIntoView(index);
            }
        });
        if (open) {
            (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(undefined);
        }
        return () => clearTimeout(timeoutId);
    }, [open]);
    const onSelectValue = (value) => {
        if (value !== undefined) {
            onSelect(value, { selected: true });
        }
        if (!multiple) {
            onToggleOpen(false);
        }
    };
    React.useImperativeHandle(ref, () => ({
        onKeyDown: event => {
            const { which } = event;
            switch (which) {
                case KeyCode.UP:
                case KeyCode.DOWN: {
                    let offset = 0;
                    if (which === KeyCode.UP) {
                        offset = -1;
                    }
                    else if (which === KeyCode.DOWN) {
                        offset = 1;
                    }
                    if (offset !== 0) {
                        const nextActiveIndex = getEnabledActiveIndex(activeIndex + offset, offset);
                        scrollIntoView(nextActiveIndex);
                        setActive(nextActiveIndex, true);
                    }
                    break;
                }
                case KeyCode.ENTER: {
                    const item = memoFlattenOptions[activeIndex];
                    if (item && !item.data.disabled) {
                        onSelectValue(item.data.value);
                    }
                    else {
                        onSelectValue(undefined);
                    }
                    if (open) {
                        event.preventDefault();
                    }
                    break;
                }
                case KeyCode.ESC: {
                    onToggleOpen(false);
                    if (open) {
                        event.stopPropagation();
                    }
                }
            }
        },
        onKeyUp: () => { },
        scrollTo: index => {
            scrollIntoView(index);
        },
    }));
    if (memoFlattenOptions.length === 0) {
        return (React.createElement("div", { role: "listbox", id: `${id}_list`, className: `${itemPrefixCls}-empty`, onMouseDown: onListMouseDown }, notFoundContent));
    }
    function renderItem(index) {
        const item = memoFlattenOptions[index];
        if (!item)
            return null;
        const itemData = (item.data || {});
        const { value, label, children } = itemData;
        const attrs = pickAttrs(itemData, true);
        const mergedLabel = childrenAsData ? children : label;
        return item ? (React.createElement("div", Object.assign({ "aria-label": typeof mergedLabel === 'string' ? mergedLabel : null }, attrs, { key: index, role: "option", id: `${id}_list_${index}`, "aria-selected": values.has(value) }), value)) : null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { role: "listbox", id: `${id}_list`, style: { height: 0, width: 0, overflow: 'hidden' } },
            renderItem(activeIndex - 1),
            renderItem(activeIndex),
            renderItem(activeIndex + 1)),
        React.createElement(List, { itemKey: "key", ref: listRef, data: memoFlattenOptions, height: height, itemHeight: itemHeight, fullHeight: false, onMouseDown: onListMouseDown, onScroll: onScroll, virtual: virtual, onMouseEnter: onMouseEnter }, ({ group, groupOption, data }, itemIndex) => {
            const { label, key } = data;
            if (group) {
                return React.createElement("div", { className: classNames(itemPrefixCls, `${itemPrefixCls}-group`) }, label !== undefined ? label : key);
            }
            const _a = data, { disabled, value, title, children, style, className } = _a, otherProps = __rest(_a, ["disabled", "value", "title", "children", "style", "className"]);
            const optionPrefixCls = `${itemPrefixCls}-option`;
            const optionClassName = classNames(itemPrefixCls, optionPrefixCls, className, {
                [`${optionPrefixCls}-grouped`]: groupOption,
                [`${optionPrefixCls}-active`]: activeIndex === itemIndex && !disabled,
                [`${optionPrefixCls}-disabled`]: disabled,
            });
            const mergedLabel = childrenAsData ? children : label;
            const content = mergedLabel || value;
            let optionTitle = typeof content === 'string' || typeof content === 'number' ? content.toString() : undefined;
            if (title !== undefined) {
                optionTitle = title;
            }
            return (React.createElement("div", Object.assign({}, otherProps, { className: optionClassName, title: optionTitle, onMouseMove: () => {
                    if (activeIndex === itemIndex || disabled) {
                        return;
                    }
                    setActive(itemIndex);
                }, onClick: () => {
                    if (!disabled) {
                        onSelectValue(value);
                    }
                }, style: style }),
                React.createElement("div", { className: `${optionPrefixCls}-content` }, content)));
        })));
};
const RefOptionList = React.forwardRef(OptionList);
RefOptionList.displayName = 'OptionList';
export default RefOptionList;
